import { useState } from "react";
import { Capacitor } from "@capacitor/core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { Passkey } from "@better-auth/passkey";
import { Button, useTranslate } from "@tovi/ui";
import { api } from "@/lib/api.ts";
import { authClient } from "@/lib/auth-client.ts";
import { addPasskey, passkeySupported, type PasskeyOutcome } from "@/lib/passkey.ts";
import { scanTillCode } from "@/lib/scan.ts";

type Me = {
  user: { displayName: string | null; username: string | null };
  store: { name: string } | null;
};

/**
 * The employee app's home screen (`#166`).
 *
 * Everything this app will eventually do — check-in, scanning, taking an
 * order — opens from here. For now it says who is signed in and where, which
 * is the one thing worth confirming before any of that exists: `GET /me` is
 * the same self-scoped endpoint every other realm opens on. It also offers to
 * register a passkey (`#243`) where the native passkey plugin reports a
 * platform authenticator (`#268`) and the employee has none yet — the
 * credential the product treats as primary, with nothing before this screen
 * able to set it.
 */
export function HomeScreen({
  onSignedOut,
  onEnterCode,
}: {
  onSignedOut: () => void;
  onEnterCode: (userCode: string) => void;
}) {
  const t = useTranslate();
  const queryClient = useQueryClient();
  const me = useQuery({ queryKey: ["me"], queryFn: () => api<Me>("/me") });
  const passkeyAvailable = useQuery({
    queryKey: ["passkey-available"],
    queryFn: passkeySupported,
    staleTime: Infinity,
  });
  const passkeys = useQuery({
    queryKey: ["passkeys"],
    queryFn: async () => {
      const result = await authClient.$fetch<Passkey[]>("/passkey/list-user-passkeys", {
        method: "GET",
        throw: false,
      });
      if (result.error) throw result.error;
      return result.data;
    },
    enabled: passkeyAvailable.data ?? false,
  });
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState<PasskeyOutcome | null>(null);
  const [scanning, setScanning] = useState(false);
  const [scanRefused, setScanRefused] = useState(false);

  async function handleAddPasskey() {
    setBusy(true);
    const result = await addPasskey();
    setBusy(false);
    setOutcome(result === "cancelled" ? null : result);
    if (result === "added") void queryClient.invalidateQueries({ queryKey: ["passkeys"] });
  }

  async function handleScan() {
    setScanning(true);
    setScanRefused(false);
    const result = await scanTillCode();
    setScanning(false);
    if (result === "cancelled") return;
    if (result === null) {
      setScanRefused(true);
      return;
    }
    onEnterCode(result);
  }

  const offerPasskey = (passkeyAvailable.data ?? false) && passkeys.data?.length === 0;

  return (
    <div className="flex h-full flex-col items-center justify-center gap-2 p-6 text-center">
      <p className="text-sm text-ink-muted">{t("staff.home.greeting")}</p>
      <h1 className="text-xl font-semibold text-ink-body">
        {me.data?.user.displayName ?? me.data?.user.username ?? ""}
      </h1>
      <p className="text-sm text-ink-muted">{me.data?.store?.name ?? ""}</p>
      <Button variant="ghost" onClick={onSignedOut} className="mt-6">
        {t("staff.home.signOut")}
      </Button>

      {offerPasskey ? (
        <div className="mt-6 flex w-full max-w-xs flex-col gap-2 border-t border-line pt-6">
          <p className="text-sm text-ink-muted">{t("staff.home.passkeyOffer")}</p>
          <Button variant="secondary" loading={busy} onClick={() => void handleAddPasskey()}>
            {t("staff.home.passkeyAdd")}
          </Button>
          {outcome === "added" ? (
            <p className="text-sm text-ink-muted">{t("staff.home.passkeyAdded")}</p>
          ) : null}
          {outcome === "stale" ? (
            <p className="text-sm text-danger">{t("staff.home.passkeyStale")}</p>
          ) : null}
          {outcome === "failed" ? (
            <p className="text-sm text-danger">{t("staff.home.passkeyFailed")}</p>
          ) : null}
        </div>
      ) : null}

      {Capacitor.isNativePlatform() ? (
        <div className="mt-10 flex w-full max-w-xs flex-col gap-2 border-t border-line pt-6">
          <Button loading={scanning} onClick={() => void handleScan()}>
            {t("staff.home.scan")}
          </Button>
          {scanRefused ? (
            <p className="text-sm text-danger">{t("staff.home.scanRefused")}</p>
          ) : null}
        </div>
      ) : null}

      {/* The fallback: for a camera that will not scan, and for the web
          build, which has no in-app scanner. */}
      <div className="mt-10 flex w-full max-w-xs flex-col gap-2 border-t border-line pt-6">
        <p className="text-sm text-ink-muted">{t("staff.home.enterCode")}</p>
        <input
          className="h-12 rounded-control border border-line bg-surface px-3 text-center text-ink-body"
          placeholder={t("staff.home.enterCodePlaceholder")}
          value={code}
          onChange={(event) => setCode(event.target.value)}
        />
        <Button
          variant="secondary"
          disabled={!code.trim()}
          onClick={() => onEnterCode(code.trim())}
        >
          {t("staff.home.enterCodeSubmit")}
        </Button>
      </div>
    </div>
  );
}
